import { useState, useCallback } from 'react';
import { SelectedChartForArgumentT } from '../Analyse';
import { useGetArguments } from '../../Arguments/hooks/arguments';
import { ArgumentT } from 'routes/BudgetModule/config/types';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { trackEventBudget, TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import { createWidget } from 'lib/layout/provider/backend';
import { WidgetT } from 'routes/Dashboard/components/WidgetsLayout/helpers';
import { ChartDataT } from 'routes/Explore/hooks/updateChartThenUpdateVizSettings';
import { Dispatch } from 'store/store';
import { useDispatch } from 'store/hooks';
import { useTranslation } from 'react-i18next';

export type CustomChartDataPayload = Pick<
    ChartDataT,
    'title' | 'settings' | 'vizType' | 'filters'
> & {
    customData: { indicatorName: string };
};

type UseAddToArgumentReturnT = {
    selectedChartForArgument: SelectedChartForArgumentT | null | undefined;
    budgetArguments: ArgumentT[];
    onAddToArgumentModalOpen: (
        selectedChart: SelectedChartForArgumentT,
        indicatorName: string,
    ) => void;
    resetSelectedChart: () => void;
    saveChartToArgument: (
        argument: ArgumentT,
        selectedChartForArgument: SelectedChartForArgumentT,
    ) => void;
};

/**
 * Creates a mutation function for saving a chart to argument.
 */
const useSaveChartMutation = (
    dispatch: Dispatch,
    trackerMetadata: {
        budgetProposal: string;
        phase: string;
        service: string;
    },
): UseMutationResult => {
    const { t } = useTranslation();
    return useMutation({
        mutationFn: ({
            id,
            chartData,
            position,
        }: {
            id: number;
            chartData: ChartDataT | CustomChartDataPayload;
            position: WidgetT['position'];
        }) =>
            createWidget('argument', id, {
                type: 'chart',
                chart: chartData,
                position,
            }),

        onSuccess: () => {
            dispatch.vision.notifyUser({
                message: t('budgetProposal.savedInArgument'),
                snackType: 'success',
            });
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.analyse.argumentSave,
                trackerMetadata,
            );
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};

/**
 * Hook to save a selected chart to argument using the specified fetching method.
 * @param dispatch The dispatch function.
 * @param trackerMetadata Metadata for tracking events.
 */
const useSaveChartToArgument = (
    dispatch: Dispatch,
    trackerMetadata: {
        budgetProposal: string;
        phase: string;
        service: string;
    },
    indicatorName: string = 'budget_analysis',
) => {
    const saveChartMutation = useSaveChartMutation(dispatch, trackerMetadata);

    const saveChartToArgument = (
        argument: ArgumentT,
        selectedChartForArgument: SelectedChartForArgumentT,
    ): void => {
        const { query, title, settings } = selectedChartForArgument;
        const chartData = {
            customData: { indicatorName: indicatorName },
            filters: query.filters.filter((f) => f.value !== null),
            vizType: 'pivot_table',
            title,
            settings,
        };

        const position = {
            md: {
                w: 10,
                h: 10,
            },
            xs: {
                w: 10,
                h: 10,
            },
        };

        // hack: XXX legendPosition is not a setting known by the backend
        delete chartData.settings.legendPosition;
        saveChartMutation.mutate({
            id: argument.layoutId,
            chartData,
            position,
        });
    };

    return { saveChartToArgument };
};

/**
 * Custom hook to manage the state of the selected chart, fetch budget arguments, handle modal open and save chart to argument.
 *
 * @returns - Returns the selected chart state, budget arguments, modal open handler, and a method to reset the selected chart.
 */
export const useAddToArgument = (
    budgetProposalId: string,
    resourceSoftwareId: string,
    trackerMetadata: {
        budgetProposal: string;
        phase: string;
        service: string;
    },
): UseAddToArgumentReturnT => {
    const dispatch = useDispatch();
    const [selectedChartForArgument, setSelectedChartForArgument] = useState<
        SelectedChartForArgumentT | null | undefined
    >(null);
    const [indicatorName, setIndicatorName] =
        useState<string>('budget_analysis');
    const { data: budgetArguments = [] } = useGetArguments({
        budgetProposalId,
        resourceSoftwareId,
    });

    const onAddToArgumentModalOpen = useCallback(
        (selectedChart: SelectedChartForArgumentT, indicator: string) => {
            setSelectedChartForArgument(selectedChart);
            setIndicatorName(indicator);
        },
        [],
    );

    const resetSelectedChart = useCallback(() => {
        setSelectedChartForArgument(null);
    }, []);

    const { saveChartToArgument } = useSaveChartToArgument(
        dispatch,
        trackerMetadata,
        indicatorName,
    );

    return {
        selectedChartForArgument,
        budgetArguments,
        onAddToArgumentModalOpen,
        resetSelectedChart,
        saveChartToArgument,
    };
};
